import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useState } from 'react';

import MenuIcon from '@mui/icons-material/Menu';
import { useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Toolbar from '@mui/material/Toolbar';
import { noop } from 'lodash';
import { useTranslation } from 'next-i18next';
import config from 'src/config';

import CartIcon from './CartIcon';

import Button from '@components/atoms/button';
import CategoryMenu from '@components/atoms/categoryMenu';
import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import NextLink from '@components/atoms/link';
import Logo from '@components/atoms/logo';
import MyCourseMenu from '@components/molecules/myCourseMenu';
import NotificationBell from '@components/molecules/notification/notificationBell';
import SearchBar from '@components/molecules/search/searchBar';
import UniversityMasterDegreeMenuPopover from '@components/molecules/universityMasterDegreeMenuPopover';
import HeaderStyle from '@components/organisms/header/style';
import AuthorizeModal from '@components/share/modal/authorizeModal/AuthorizeModal';
import { login, register } from '@helpers/auth';
import { GENERAL_MENU, UserType } from '@helpers/constants';
import { useWindowSize, useBooleanReduxState, useProfile, useAppData } from '@helpers/use';
import useDialogWithoutUI from '@hooks/useDialog';
import { useNotification } from '@hooks/useNotification';

const UserMenuDrawer = dynamic(() => import('@components/molecules/userMenuDrawer'));
const NotificationDialog = dynamic(() => import('@components/molecules/notification/notificationDialog'));
const UserMenuPopover = dynamic(() => import('@components/molecules/userMenuPopover'));
const SearchDrawer = dynamic(() => import('@components/molecules/search/searchDrawer'));

type HeaderProps = {
  isHideSearch: boolean;
  onClickCartIcon?: typeof noop;
};
const DesktopHeader = ({ isHideSearch, onClickCartIcon }: HeaderProps) => {
  const { profile, isLoading } = useProfile();
  const { appData } = useAppData();
  const { t } = useTranslation();
  const theme = useTheme();

  const menuAction = profile ? (
    <>
      {[UserType.STUDENT, UserType.INSTRUCTOR, UserType.SYSTEM_ADMIN].includes(profile.role as UserType) && (
        <>
          <Box marginRight={2}>
            <MyCourseMenu />
          </Box>
          {appData?.isCartEnabled && (
            <Box marginRight={2}>
              <CartIcon onClick={onClickCartIcon} />
            </Box>
          )}
          <Box marginRight={2}>
            <NotificationBell />
          </Box>
        </>
      )}
      <UserMenuPopover />
    </>
  ) : (
    <>
      {appData?.isCartEnabled && (
        <Box mr={1}>
          <CartIcon onClick={onClickCartIcon} />
        </Box>
      )}
      <Box width={100} mr={1}>
        <Button data-testid="loginButton" onClick={() => login()} variant="outlined" color="primary" fullWidth>
          {t('login')}
        </Button>
      </Box>
      <Box width={100}>
        <Button
          data-testid="registerButton"
          onClick={() => register()}
          className="register-button"
          variant="contained"
          color="primary"
          fullWidth
        >
          {t('register')}
        </Button>
      </Box>
    </>
  );

  return (
    <AppBar position="static" className="desktop-appbar">
      <Toolbar>
        <Logo />
        <CategoryMenu />
        {isHideSearch ? <Box sx={{ flex: '1 0.99 auto' }} /> : <SearchBar />}
        {GENERAL_MENU.map((menu) => {
          if (menu.name === 'master_degree') {
            return <UniversityMasterDegreeMenuPopover key={menu.name} />;
          }

          const link = menu.name === 'teach' ? appData?.footer?.teachWithUsUrl : menu.link;
          const isExternalLink = link?.startsWith('http');

          return (
            <NextLink href={link || ''} target={isExternalLink ? '_blank' : undefined} key={menu.name}>
              <Button style={{ textTransform: 'none', color: theme.palette.common.black }}>{t(menu.tName)}</Button>
            </NextLink>
          );
        })}
        <Divider className="divider" orientation="vertical" />
        <Box display="flex" alignItems="center">
          {isLoading ? (
            <Box width={100}>
              <Skeleton animation="wave" height={10} />
              <Skeleton animation="wave" height={10} />
              <Skeleton animation="wave" height={10} />
            </Box>
          ) : (
            menuAction
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const MobileHeader = ({ isHideSearch, onClickCartIcon }: HeaderProps) => {
  const { width } = useWindowSize();
  const { profile, isLoading } = useProfile();
  const { appData } = useAppData();
  const theme = useTheme();
  const [isUserMenuDrawerOpen, setIsUserMenuDrawerOpen] = useBooleanReduxState(
    'isUserMenuDrawerOpen',
    'set_user_menu_drawer_open',
  );
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { isNotificationSeen, seeNotification } = useNotification();

  useEffect(() => {
    if (isUserMenuDrawerOpen) setIsUserMenuDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const closeDrawer = () => {
    setIsUserMenuDrawerOpen(false);
  };

  const openDrawer = () => {
    setIsUserMenuDrawerOpen(true);
  };

  const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => setIsSearchOpen(false);

  return (
    <AppBar position="static" className="mobile-appbar">
      <NotificationDialog />
      <Grid container direction="row" alignItems="center">
        <Grid item xs={2} className="menu-icon-container">
          <IconButton color="primary" onClick={openDrawer}>
            <MenuIcon />
            {!isNotificationSeen && <div className="alert-dot" />}
          </IconButton>
          <UserMenuDrawer
            profile={profile}
            isLoading={isLoading}
            isOpen={isUserMenuDrawerOpen}
            onClose={closeDrawer}
            isNotificationSeen={isNotificationSeen}
            seeNotification={seeNotification}
          />
        </Grid>
        <Grid item xs={8} container justifyContent="left">
          <Logo />
        </Grid>

        <Grid item xs={2} className="menu-icon-container-right">
          {!isHideSearch && (
            <Fragment>
              <IconButton onClick={openSearch}>
                <FontAwesomeIcon
                  style={{ fontSize: '23px' }}
                  color={theme.palette.primary.main}
                  icon="fa-light fa-magnifying-glass"
                />
              </IconButton>
              <Divider className="divider" orientation="vertical" />
            </Fragment>
          )}
          {appData?.isCartEnabled && <CartIcon onClick={onClickCartIcon} />}
        </Grid>
      </Grid>
      <SearchDrawer isOpen={isSearchOpen} onClose={closeSearch} />
    </AppBar>
  );
};

const Header = ({ hideHeaderOnScroll = false }) => {
  const router = useRouter();
  const currentUrl = new URL(router.asPath, config.APP_DOMAIN);

  const [redirectUrl, setRedirectUrl] = useState(currentUrl);

  const isHideSearch = router.pathname.includes('/cpd') && !router.pathname.includes('/cpd/courses');
  const authorizeDialog = useDialogWithoutUI({});

  const { profile } = useProfile();

  const handleClickCartIcon = async () => {
    const url = new URL('/cart', config.APP_DOMAIN);

    if (!profile) {
      authorizeDialog.onOpen();
      setRedirectUrl(url);
      return;
    }

    router.push(url.toString());
  };

  const handleModalAuthorize = () => {
    setRedirectUrl(currentUrl);
    authorizeDialog.onClose();
  };

  return (
    <HeaderStyle hideHeaderOnScroll={hideHeaderOnScroll}>
      <AuthorizeModal
        open={authorizeDialog.isOpened}
        onClose={handleModalAuthorize}
        currentUrl={redirectUrl.toString()}
      />
      <DesktopHeader isHideSearch={isHideSearch} onClickCartIcon={handleClickCartIcon} />
      <MobileHeader isHideSearch={isHideSearch} onClickCartIcon={handleClickCartIcon} />
    </HeaderStyle>
  );
};

export default Header;
